<template>
  <div class="media-processing">
    <!-- Header -->
    <div class="level mb-4">
      <div class="level-left">
        <div class="level-item">
          <h2 class="title is-4">
            <b-icon icon="link-variant" class="mr-2"></b-icon>
            Workflow Chains
          </h2>
          <p class="subtitle is-6 ml-3 has-text-grey">
            Create and manage reusable processing pipelines
          </p>
        </div>
      </div>
      <div class="level-right">
        <div class="level-item">
          <b-button
            type="is-info"
            icon-left="list"
            @click="showJobsModal = true"
          >
            View Jobs
          </b-button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="columns">
      <!-- Left: Input Selection -->
      <div class="column is-5">
        <div class="box">
          <h3 class="title is-5 mb-4">
            <b-icon icon="folder-open" class="mr-2"></b-icon>
            1. Select Input
          </h3>

          <!-- Storage Selection -->
          <b-field label="Input Storage">
            <b-select
              v-model="selectedStorageId"
              placeholder="Select storage location"
              expanded
              @input="onStorageChange"
            >
              <option
                v-for="storage in storageConfigs"
                :key="storage.id"
                :value="storage.id"
              >
                {{ storage.config_name }} ({{ storage.storage_type }})
              </option>
            </b-select>
          </b-field>

          <!-- Path Browser -->
          <b-field label="File Path">
            <b-input
              v-model="inputPath"
              placeholder="/path/to/file.mp4"
              expanded
            ></b-input>
          </b-field>

          <b-button
            type="is-light"
            icon-left="folder"
            @click="browsePath"
            :disabled="!selectedStorageId"
          >
            Browse...
          </b-button>

          <!-- Selected Files -->
          <div v-if="selectedFiles.length > 0" class="mt-4">
            <h4 class="subtitle is-6">Selected Files ({{ selectedFiles.length }})</h4>
            <div class="selected-files">
              <div
                v-for="(file, index) in selectedFiles"
                :key="index"
                class="selected-file"
              >
                <b-icon icon="file-video" size="is-small"></b-icon>
                <span class="file-name">{{ file }}</span>
                <b-button
                  type="is-text"
                  size="is-small"
                  icon-left="times"
                  @click="removeFile(index)"
                ></b-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Middle: Workflow Selection -->
      <div class="column is-4">
        <div class="box">
          <h3 class="title is-5 mb-4">
            <b-icon icon="sitemap" class="mr-2"></b-icon>
            2. Choose Workflow
          </h3>

          <!-- Quick Chains -->
          <div class="workflow-chains mb-4">
            <label class="label">Quick Chains</label>
            <div class="buttons">
              <b-button
                v-for="chain in workflowChains"
                :key="chain.id"
                :type="selectedChainId === chain.id ? 'is-primary' : 'is-light'"
                size="is-small"
                @click="selectChain(chain)"
              >
                {{ chain.name }}
              </b-button>
            </div>
          </div>

          <b-field label="Or Build Custom">
            <div class="workflow-steps">
              <b-checkbox
                v-model="selectedSteps.stitch"
                :disabled="!availableWorkflows.stitch"
              >
                <b-icon icon="link" size="is-small"></b-icon>
                Stitch Segments
              </b-checkbox>
              <b-checkbox
                v-model="selectedSteps.qc"
                :disabled="!availableWorkflows.qc"
              >
                <b-icon icon="check-circle" size="is-small"></b-icon>
                QC Analysis
              </b-checkbox>
              <b-checkbox
                v-model="selectedSteps.transcode"
                :disabled="!availableWorkflows.transcode"
              >
                <b-icon icon="exchange-alt" size="is-small"></b-icon>
                Transcode
              </b-checkbox>
              <b-checkbox
                v-model="selectedSteps.normalize"
                :disabled="!availableWorkflows.normalize"
              >
                <b-icon icon="volume-up" size="is-small"></b-icon>
                Loudness Normalize
              </b-checkbox>
              <b-checkbox
                v-model="selectedSteps.vod"
                :disabled="!availableWorkflows.vod"
              >
                <b-icon icon="cloud-upload-alt" size="is-small"></b-icon>
                VOD Delivery
              </b-checkbox>
            </div>
          </b-field>

          <!-- Workflow Preview -->
          <div v-if="workflowPreview.length > 0" class="workflow-preview mt-4">
            <label class="label">Workflow Pipeline</label>
            <div class="pipeline">
              <span
                v-for="(step, index) in workflowPreview"
                :key="index"
                class="pipeline-step"
              >
                <b-tag :type="getStepTagType(step)">{{ step }}</b-tag>
                <b-icon
                  v-if="index < workflowPreview.length - 1"
                  icon="arrow-right"
                  size="is-small"
                  class="mx-1"
                ></b-icon>
              </span>
            </div>
          </div>

          <!-- Save as Chain -->
          <div class="mt-4" v-if="workflowPreview.length > 1">
            <b-button
              type="is-text"
              size="is-small"
              icon-left="save"
              @click="showSaveChainModal = true"
            >
              Save as Chain
            </b-button>
          </div>
        </div>
      </div>

      <!-- Right: Options & Submit -->
      <div class="column is-3">
        <div class="box">
          <h3 class="title is-5 mb-4">
            <b-icon icon="sliders-h" class="mr-2"></b-icon>
            3. Options
          </h3>

          <!-- Encoding Preset -->
          <b-field label="Encoding Preset" v-if="showEncodingOptions">
            <b-select
              v-model="encodingPresetId"
              placeholder="Select preset"
              expanded
            >
              <option :value="null">Default</option>
              <option
                v-for="preset in encodingPresets"
                :key="preset.id"
                :value="preset.id"
              >
                {{ preset.preset_name }}
              </option>
            </b-select>
          </b-field>

          <!-- Output Storage -->
          <b-field label="Output Storage" v-if="showOutputOptions">
            <b-select
              v-model="outputStorageId"
              placeholder="Same as input"
              expanded
            >
              <option :value="null">Same as input</option>
              <option
                v-for="storage in storageConfigs"
                :key="storage.id"
                :value="storage.id"
              >
                {{ storage.config_name }}
              </option>
            </b-select>
          </b-field>

          <!-- Loudness Standard -->
          <b-field label="Loudness Standard" v-if="selectedSteps.normalize">
            <b-select v-model="loudnessStandard" expanded>
              <option value="ebu_r128">EBU R128 (-23 LUFS)</option>
              <option value="atsc_a85">ATSC A/85 (-24 LUFS)</option>
              <option value="op59">OP-59 (-24 LUFS)</option>
            </b-select>
          </b-field>

          <hr>

          <!-- Submit Button -->
          <b-button
            type="is-primary"
            icon-left="play"
            expanded
            :loading="submitting"
            :disabled="!canSubmit"
            @click="submitJob"
          >
            Start Processing
          </b-button>

          <p class="help mt-2" v-if="!canSubmit">
            Select input file and at least one workflow step
          </p>
        </div>
      </div>
    </div>

    <!-- Browse Modal -->
    <b-modal
      v-model="showBrowseModal"
      has-modal-card
      trap-focus
      :width="800"
    >
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Browse Storage</p>
          <button type="button" class="delete" @click="showBrowseModal = false"></button>
        </header>
        <section class="modal-card-body">
          <!-- Browser content would go here -->
          <p>Storage browser component would be embedded here</p>
        </section>
      </div>
    </b-modal>

    <!-- Jobs Modal -->
    <b-modal
      v-model="showJobsModal"
      has-modal-card
      trap-focus
      :width="900"
    >
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Processing Jobs</p>
          <button type="button" class="delete" @click="showJobsModal = false"></button>
        </header>
        <section class="modal-card-body">
          <b-table :data="jobs" :loading="loadingJobs">
            <b-table-column field="id" label="ID" width="60" v-slot="props">
              {{ props.row.id }}
            </b-table-column>
            <b-table-column field="type" label="Type" v-slot="props">
              <b-tag>{{ props.row.type }}</b-tag>
            </b-table-column>
            <b-table-column field="status" label="Status" v-slot="props">
              <b-tag :type="getStatusType(props.row.status)">
                {{ props.row.status }}
              </b-tag>
            </b-table-column>
            <b-table-column field="inputPath" label="Input" v-slot="props">
              <span class="file-path">{{ props.row.inputPath }}</span>
            </b-table-column>
            <b-table-column field="createdAt" label="Created" v-slot="props">
              {{ formatDate(props.row.createdAt) }}
            </b-table-column>
            <b-table-column field="actions" label="" width="100" v-slot="props">
              <b-button
                v-if="props.row.status === 'queued' || props.row.status === 'processing'"
                type="is-danger"
                size="is-small"
                icon-left="times"
                @click="cancelJob(props.row.id)"
              >
                Cancel
              </b-button>
            </b-table-column>
          </b-table>
        </section>
      </div>
    </b-modal>

    <!-- Save Chain Modal -->
    <b-modal
      v-model="showSaveChainModal"
      has-modal-card
      trap-focus
    >
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Save Workflow Chain</p>
          <button type="button" class="delete" @click="showSaveChainModal = false"></button>
        </header>
        <section class="modal-card-body">
          <b-field label="Chain Name">
            <b-input v-model="newChainName" placeholder="e.g., My TX Pipeline"></b-input>
          </b-field>
          <b-field label="Description">
            <b-input v-model="newChainDescription" type="textarea" placeholder="What this chain does..."></b-input>
          </b-field>
          <div class="mt-3">
            <strong>Steps:</strong>
            <span v-for="(step, i) in workflowPreview" :key="i">
              {{ step }}<span v-if="i < workflowPreview.length - 1"> → </span>
            </span>
          </div>
        </section>
        <footer class="modal-card-foot">
          <b-button @click="showSaveChainModal = false">Cancel</b-button>
          <b-button type="is-primary" @click="saveChain" :loading="savingChain">
            Save Chain
          </b-button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import mediaProcessingApi from '@/api/mediaProcessing'
import storageConfigApi from '@/api/storageConfig'
import encodingPresetsApi from '@/api/encodingPresets'
import { format } from 'date-fns'

export default {
  name: 'MediaProcessing',

  props: {
    // Optional: Pre-select media/episode for linking
    mediaId: { type: Number, default: null },
    episodeId: { type: Number, default: null },
    // Optional: Pre-fill input path
    initialPath: { type: String, default: '' },
    initialStorageId: { type: Number, default: null }
  },

  data () {
    return {
      // Storage & Input
      storageConfigs: [],
      selectedStorageId: null,
      inputPath: '',
      selectedFiles: [],

      // Workflows
      availableWorkflows: {},
      workflowChains: [],
      selectedChainId: null,
      selectedSteps: {
        stitch: false,
        qc: false,
        transcode: false,
        normalize: false,
        vod: false
      },

      // Options
      encodingPresets: [],
      encodingPresetId: null,
      outputStorageId: null,
      loudnessStandard: 'ebu_r128',

      // Jobs
      jobs: [],
      loadingJobs: false,

      // UI State
      showBrowseModal: false,
      showJobsModal: false,
      showSaveChainModal: false,
      submitting: false,
      savingChain: false,
      newChainName: '',
      newChainDescription: ''
    }
  },

  computed: {
    canSubmit () {
      return (
        this.selectedStorageId &&
        (this.inputPath || this.selectedFiles.length > 0) &&
        (this.selectedChainId || this.workflowPreview.length > 0)
      )
    },

    workflowPreview () {
      if (this.selectedChainId) {
        const chain = this.workflowChains.find(c => c.id === this.selectedChainId)
        return chain ? chain.steps.map(s => s.type) : []
      }

      const steps = []
      if (this.selectedSteps.stitch) steps.push('stitch')
      if (this.selectedSteps.qc) steps.push('qc')
      if (this.selectedSteps.transcode) steps.push('transcode')
      if (this.selectedSteps.normalize) steps.push('normalize')
      if (this.selectedSteps.vod) steps.push('vod')
      return steps
    },

    showEncodingOptions () {
      return this.selectedSteps.stitch || this.selectedSteps.transcode
    },

    showOutputOptions () {
      return this.selectedSteps.stitch || this.selectedSteps.transcode || this.selectedSteps.vod
    }
  },

  async mounted () {
    await this.loadData()

    // Apply initial values if provided
    if (this.initialStorageId) {
      this.selectedStorageId = this.initialStorageId
    }
    if (this.initialPath) {
      this.inputPath = this.initialPath
    }
  },

  methods: {
    async loadData () {
      try {
        // Load storage configs
        const storageResult = await storageConfigApi.getAll()
        this.storageConfigs = storageResult.storageConfigs || storageResult

        // Load available workflows
        const workflowResult = await mediaProcessingApi.getAvailableWorkflows()
        if (workflowResult.workflows) {
          this.availableWorkflows = {}
          workflowResult.workflows.forEach(wf => {
            this.availableWorkflows[wf.type] = wf
          })
        }

        // Load workflow chains
        const chainsResult = await mediaProcessingApi.getWorkflowChains()
        this.workflowChains = chainsResult.chains || []

        // Load encoding presets
        const presetsResult = await encodingPresetsApi.getAll()
        this.encodingPresets = presetsResult.presets || presetsResult
      } catch (error) {
        console.error('Error loading data:', error)
        this.$buefy.toast.open({
          message: 'Error loading data',
          type: 'is-danger'
        })
      }
    },

    onStorageChange () {
      // Clear selected files when storage changes
      this.selectedFiles = []
      this.inputPath = ''
    },

    browsePath () {
      this.showBrowseModal = true
    },

    removeFile (index) {
      this.selectedFiles.splice(index, 1)
    },

    selectChain (chain) {
      if (this.selectedChainId === chain.id) {
        this.selectedChainId = null
      } else {
        this.selectedChainId = chain.id
        // Clear custom steps when selecting a chain
        Object.keys(this.selectedSteps).forEach(k => {
          this.selectedSteps[k] = false
        })
      }
    },

    getStepTagType (step) {
      const types = {
        stitch: 'is-info',
        qc: 'is-success',
        transcode: 'is-warning',
        normalize: 'is-primary',
        vod: 'is-link'
      }
      return types[step] || 'is-light'
    },

    getStatusType (status) {
      const types = {
        queued: 'is-info',
        processing: 'is-warning',
        completed: 'is-success',
        failed: 'is-danger',
        cancelled: 'is-light'
      }
      return types[status] || 'is-light'
    },

    formatDate (date) {
      if (!date) return '-'
      return format(new Date(date), 'MMM d, HH:mm')
    },

    async submitJob () {
      this.submitting = true

      try {
        const params = {
          inputStorageConfigId: this.selectedStorageId,
          inputPath: this.inputPath || this.selectedFiles[0],
          outputStorageConfigId: this.outputStorageId,
          encodingPresetId: this.encodingPresetId,
          mediaId: this.mediaId,
          episodeId: this.episodeId,
          loudnessStandard: this.loudnessStandard
        }

        if (this.selectedChainId) {
          params.chainId = this.selectedChainId
        } else {
          params.workflowSteps = this.workflowPreview.map(type => ({ type }))
        }

        const result = await mediaProcessingApi.submitJob(params)

        this.$buefy.toast.open({
          message: result.message || 'Job submitted successfully',
          type: 'is-success'
        })

        // Clear form
        this.inputPath = ''
        this.selectedFiles = []

        // Refresh jobs
        this.loadJobs()
      } catch (error) {
        console.error('Error submitting job:', error)
        this.$buefy.toast.open({
          message: error.response?.data?.error || 'Error submitting job',
          type: 'is-danger'
        })
      } finally {
        this.submitting = false
      }
    },

    async loadJobs () {
      this.loadingJobs = true
      try {
        const result = await mediaProcessingApi.listJobs({ limit: 50 })
        this.jobs = result.jobs || []
      } catch (error) {
        console.error('Error loading jobs:', error)
      } finally {
        this.loadingJobs = false
      }
    },

    async cancelJob (jobId) {
      try {
        await mediaProcessingApi.cancelJob(jobId)
        this.$buefy.toast.open({
          message: 'Job cancelled',
          type: 'is-success'
        })
        this.loadJobs()
      } catch (error) {
        this.$buefy.toast.open({
          message: error.response?.data?.error || 'Error cancelling job',
          type: 'is-danger'
        })
      }
    },

    async saveChain () {
      if (!this.newChainName) {
        this.$buefy.toast.open({
          message: 'Please enter a chain name',
          type: 'is-warning'
        })
        return
      }

      this.savingChain = true
      try {
        const steps = this.workflowPreview.map(type => ({ type }))
        await mediaProcessingApi.saveWorkflowChain({
          name: this.newChainName,
          description: this.newChainDescription,
          steps
        })

        this.$buefy.toast.open({
          message: 'Workflow chain saved',
          type: 'is-success'
        })

        this.showSaveChainModal = false
        this.newChainName = ''
        this.newChainDescription = ''

        // Reload chains
        const chainsResult = await mediaProcessingApi.getWorkflowChains()
        this.workflowChains = chainsResult.chains || []
      } catch (error) {
        this.$buefy.toast.open({
          message: error.response?.data?.error || 'Error saving chain',
          type: 'is-danger'
        })
      } finally {
        this.savingChain = false
      }
    }
  },

  watch: {
    showJobsModal (val) {
      if (val) {
        this.loadJobs()
      }
    }
  }
}
</script>

<style scoped>
.media-processing {
  padding: 1rem;
}

.workflow-steps .b-checkbox {
  display: block;
  margin-bottom: 0.75rem;
}

.workflow-preview {
  background: #f5f5f5;
  padding: 0.75rem;
  border-radius: 4px;
}

.pipeline {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.pipeline-step {
  display: flex;
  align-items: center;
}

.selected-files {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 0.5rem;
}

.selected-file {
  display: flex;
  align-items: center;
  padding: 0.25rem 0;
  border-bottom: 1px solid #eee;
}

.selected-file:last-child {
  border-bottom: none;
}

.selected-file .file-name {
  flex: 1;
  margin-left: 0.5rem;
  font-size: 0.875rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-path {
  font-size: 0.75rem;
  color: #666;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
}
</style>
